'use client'

import React, { useState } from 'react'
import { FiStar, FiGlobe, FiMap } from 'react-icons/fi'
import { Button, Tooltip } from '@/components/ui'
import { addCartClient } from '@/lib/actions/client'
import { FaMinus, FaPlus } from 'react-icons/fa'
import { changeQtyClient } from '@/lib/actions/client'
import { useCartStore } from '@/store/useCartStore'
import { MdErrorOutline, MdLocationOn, MdOutlineDone } from 'react-icons/md'
import { FiShoppingBag } from 'react-icons/fi'
import Link from 'next/link'

interface ProductBuyBoxProps {
    product: {
        id: string
        sku?: string
        name: string
        price: number
        priceCurrency?: string
        oldPrice?: number | null
        rating?: number
        reviewsCount?: number
        availableCount?: number
        location?: string
        deliveryDate?: string
        pickupDate?: string
        pickupPriceText?: string
        deliveryPriceText?: string
        isPickupAvailable?: boolean
        isInStock?: boolean

        variants: {
            id: string
            vendorCode: string
            color: string | null
            size: string | null
            price: number
            available: boolean
            currency: string
        }[]

        color: (string | null)[]
        sizes: (string | null)[]
    }
}

const COLORS_MAP: Record<string, string[]> = {
    черный: ['#000000'],
    белый: ['#FFFFFF'],
    серый: ['#888888'],
    'темный меланж': ['#555555'],
    синий: ['#0C3C78'],
    'синий/черный': ['#0C3C78', '#000000'],
    'красный/черный': ['#E74A2F', '#000000'],
    'желтый/черный': ['#FFFF00', '#000000'],
    'фиолетовый/серый': ['#A5A5A5', '#555555'],
    красный: ['#E74A2F'],
    желтый: ['#FFFF00'],
    хвойный: ['#3A454D'],
    'хвойный/черный': ['#3A454D', '#000000'],
    хаки: ['#4E5F3A'],
    розовый: ['#C65967'],
    бежевый: ['#F5F5DC'],
    'белый/серый': ['#FFFFFF', '#888888'],
    'серый/черный': ['#888888', '#000000'],
    'графит серый/черный': ['#555555', '#000000'],
    'коричневый/черный': ['#8B4513', '#000000'],
}
function formatPrice(price: number, currency = '₽') {
    return price
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
        .concat(` ${currency}`)
}

export default function ProductBuyBox({ product }: ProductBuyBoxProps) {
    const {
        sku,
        name,
        oldPrice,
        rating = 0,
        reviewsCount = 0,
        location = 'Москва, Московская область',
        priceCurrency = '₽',
    } = product

    const [selectedSize, setSelectedSize] = useState<string | null>(
        product.variants[0]?.size ?? null,
    )

    const [isError, setIsError] = useState<boolean>(false)

    //цвет

    const [selectedColor, setSelectedColor] = useState<string | null>(
        product.variants[0]?.color ?? null,
    )

    const uniqueColors = Array.from(
        new Set(product.variants.map((v) => v.color).filter(Boolean)),
    ) as string[]

    const getColorStyle = (color: string) => {
        const key = color.toLowerCase().trim()
        const mapped = COLORS_MAP[key] ?? ['#000000']

        return mapped.length === 1
            ? {
                  backgroundColor: mapped[0],
                  border: `1px solid ${mapped[0]}`,
              }
            : {
                  background: `linear-gradient(135deg, ${mapped[0]} 50%, ${mapped[1]} 50%)`,
                  border: `1px solid ${mapped[0]}`,
              }
    }

    const currentVariant =
        product.variants.find((v) => v.size === selectedSize) ??
        product.variants[0]

    const currentId = currentVariant?.id ?? ''
    const currentPrice = currentVariant?.price ?? 0
    const currentIsInStock = currentVariant?.available ?? false

    const cartItem = useCartStore((state) =>
        state.items?.find((i) => i.variant_id === currentId),
    )
    const quantity = cartItem?.quantity ?? 1
    const changeQty = async (id: string, newQty: number) => {
        if (newQty < 1) return
        const res = await changeQtyClient(id, newQty)

        if (res?.error) {
            console.error('Ошибка изменения количества', res)
            return
        }
        await useCartStore.getState().loadItems()
    }

    const handleAddToCart = async (
        id: string,
        qty: number,
        e?: React.MouseEvent,
    ) => {
        e?.stopPropagation()
        e?.preventDefault()

        setIsError(false)

        const res = await addCartClient(id, 1)

        if (!res || res.error) {
            setIsError(true)
            console.log('Ошибка при добавлении товара!', res)
            return
        }

        useCartStore.getState().loadItems()
        console.log('добавлено', res)
    }

    return (
        <aside className="w-full lg:w-[420px]">
            <div className="bg-white rounded-sm shadow-md border border-gray-100 p-6">
                <div className="text-xs text-gray-400 mb-2">
                    <span className="text-gray-600">{sku ?? '—'}</span>
                </div>

                <h1 className="text-lg md:text-xl font-semibold text-gray-900 mb-3 leading-tight">
                    {name}
                </h1>
                
                 {/* звезды отзывов */}
                {/* <div className="flex items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <div className="flex items-center gap-0.5">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <FiStar
                                    key={i}
                                    size={16}
                                    className={
                                        i < Math.round(rating)
                                            ? 'text-yellow-400 fill-yellow-400'
                                            : 'text-gray-200'
                                    }
                                />
                            ))}
                        </div>
                        <div className="text-gray-500 ml-2">
                            {reviewsCount > 0
                                ? `${reviewsCount} отзывов`
                                : 'Нет отзывов'}
                        </div>
                    </div>
                </div> */}

                <div className="mb-4">
                    <div className="flex items-end justify-between gap-4 mb-3">
                        <div>
                            <div className="text-3xl md:text-4xl font-extrabold text-gray-900">
                                {formatPrice(currentPrice, priceCurrency)}
                            </div>
                            {oldPrice && (
                                <div className="text-sm text-gray-400 line-through mt-1">
                                    {formatPrice(oldPrice, priceCurrency)}
                                </div>
                            )}
                        </div>

                        <div className="w-40">
                            {isError ? (
                                <div className="flex border py-3 px-2 rounded-lg border-error bg-red-50 items-center">
                                    <span className="text-red-600 text-sm">
                                        Ошибка при добавлении
                                    </span>
                                    <MdErrorOutline
                                        size={35}
                                        className="text-error font-bold"
                                    />
                                </div>
                            ) : cartItem ? (
                                <div className="flex items-center justify-between border border-gray-300 rounded-sm overflow-hidden">
                                    <button
                                        onClick={() =>
                                            changeQty(
                                                cartItem.id,
                                                Math.max(
                                                    1,
                                                    cartItem.quantity - 1,
                                                ),
                                            )
                                        }
                                        className="  w-10 h-10 flex items-center justify-center  text-lg font-medium hover:bg-gray-100 transition"
                                    >
                                        <FaMinus />
                                    </button>
                                    <span className="min-w-[2rem] text-center py-[10px] px-[4px] text-lg text-gray-800">
                                        <span>{cartItem.quantity}</span>
                                    </span>
                                    <button
                                        onClick={() =>
                                            changeQty(
                                                cartItem.id,
                                                cartItem.quantity + 1,
                                            )
                                        }
                                        className="w-10 h-10
                                            flex items-center justify-center text-lg font-medium hover:bg-gray-100 transition"
                                    >
                                        <FaPlus />
                                    </button>
                                </div>
                            ) : (
                                <Button
                                    className="w-full bg-gray-900 text-white py-3 rounded-lg tracking-[0.2px] text-[17px] font-semibold leading-[24px] hover:opacity-95 hover:text-white transition-shadow shadow-sm disabled:bg-gray-400 "
                                    disabled={!currentIsInStock}
                                    onClick={(e) =>
                                        handleAddToCart(currentId, 1, e)
                                    }
                                >
                                    {currentIsInStock
                                        ? 'В корзину'
                                        : 'Нет в наличии'}
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* <div className="flex items-start gap-2 text-sm text-primary mb-2">
                        <FiInfo size={16} />
                        <div>
                            <div className="font-medium">
                                Только онлайн-заказ
                            </div>
                            <div className="text-xs text-gray-500">
                                Товар не продаётся в гипермаркетах, закажите его
                                онлайн
                            </div>
                        </div>
                    </div> */}
                </div>

                {uniqueColors.length > 0 && (
                    <div className="mb-4">
                        <div className="flex items-start gap-2 text-sm font-medium text-gray-700 mb-2">
                            Цвет
                        </div>

                        <div className="flex gap-2 flex-wrap">
                            {uniqueColors.map((color) => {
                                const isActive = selectedColor === color
                                const style = getColorStyle(color)

                                return (
                                    <Tooltip key={color} title={color}>
                                        <button
                                            key={color}
                                            type="button"
                                            onClick={() =>
                                                setSelectedColor(color)
                                            }
                                            className={`
                            relative rounded-full w-6 h-6 transition-all flex items-center justify-center
                       
                        `}
                                            style={style}
                                        >
                                            {/* {isActive && (
                            <MdOutlineDone
                                size={18}
                                className="text-white drop-shadow"
                            />
                        )} */}
                                        </button>
                                    </Tooltip>
                                )
                            })}
                        </div>
                    </div>
                )}
                <div className="flex items-start gap-2 text-sm text-primary mb-2">
                    Размер
                </div>

                <div className="flex flex-wrap gap-2">
                    {product.sizes.map((size) => {
                        const variant = product.variants.find(
                            (v) => v.size === size,
                        )
                        const disabled = !variant?.available

                        const isActive = selectedSize === size

                        return (
                            <button
                                key={size ?? ''}
                                type="button"
                                disabled={disabled}
                                onClick={() => setSelectedSize(size)}
                                className={`
                    px-3 py-2 min-w-[44px] text-sm rounded-md border transition
                    ${
                        disabled
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : isActive
                              ? 'bg-fiot text-white border-[#4E51D8]'
                              : 'border-gray-300 text-gray-700 hover:border-[#4E51D8]'
                    }
                `}
                            >
                                {size}
                            </button>
                        )
                    })}
                </div>
                <hr className="my-3 border-gray-100" />

                <div className="mb-4">
                    <div className="text-sm font-medium text-green-700">
                        Доступно для заказа
                    </div>
                    <div className="text-xs text-gray-500">{location}</div>
                </div>

                <hr className="my-3 border-gray-100" />

                <div className="flex flex-col gap-3 text-sm text-gray-700">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-gray-50 rounded-md">
                                <FiShoppingBag
                                    size={18}
                                    className="text-gray-600"
                                />
                            </div>
                            <div className="text-gray-600">
                                Доступен самовывоз
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-gray-50 rounded-md">
                                <MdLocationOn
                                    size={18}
                                    className="text-gray-600"
                                />
                            </div>
                            <div>
                                <div className="font-medium text-gray-600">
                                    Доставка по Москве в пределах МКАД при
                                    заказе свыше 30 000 ₽
                                </div>
                                <div className="text-xs font-semibold text-second">
                                    Бесплатно
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-gray-50 rounded-md">
                                <FiMap size={18} className="text-gray-600" />
                            </div>
                            <div>
                                <div className="font-medium text-gray-600">
                                    Доставка за МКАД до 50 км при заказе свыше
                                    55 000 ₽
                                </div>
                                <div className="text-xs font-semibold text-second">
                                    Бесплатно
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-gray-50 rounded-md">
                                <FiGlobe size={18} className="text-gray-600" />
                            </div>
                            <div className="text-gray-600">
                                Доступна доставка по всей России
                            </div>
                        </div>
                    </div>

                    <Link
                        className="hover:underline mt-4 text-primary"
                        href="/delivery"
                    >
                        Подробнее о доставке
                    </Link>
                </div>
            </div>
        </aside>
    )
}
